/** The text that a driver sees for a refused entry: what happened and what to do, never the server text (SO-973). */

export const FIELD_TEXT = {
  copy: "The server has a different copy of this entry. Do not record it again. Speak to the office.",
  stopPickup: "The server has a pickup for this stop. It did not keep the gallons of this entry. On Route, tap Mark it done or Partly emptied for this stop.",
  changed: "The office or a different phone changed this first. Speak to the office.",
  dayOver: "The route day is over, so the stop cannot change from this phone. Ask the office to change it.",
  preTrip: "Do the pre-trip check on Route. Then open this entry and tap Save again.",
  checklist: "The office changed the checklist. Do the check again on Route.",
  deleted: "The office deleted this account. Speak to the office.",
  time: "The time of this entry is not correct. Change the time and tap Save again.",
  value: "A value in this entry is not correct. Change the entry and tap Save again.",
  rights: "You cannot record this entry. Speak to the office.",
  missing: "The server cannot find this account, route or stop. Speak to the office.",
  other: "The server did not accept this entry. Speak to the office.",
} as const;

const RULES: [RegExp, keyof typeof FIELD_TEXT][] = [
  [/^Another [\w-]+ has this id\.$|^This stop already has a pickup\.$/, "copy"],
  [/pre-trip check/i, "preTrip"],
  [/checklist changed/i, "checklist"],
  [/is deleted/i, "deleted"],
  [/Only a stop of a route of today can change/i, "dayOver"],
  [/already has a (result|correction)|on another load/i, "changed"],
  [/\b(time|date)\b.*(future|after today|day of the route)|must be in the last/i, "time"],
];

/** The server text of a second pickup for a route stop: the server keeps the first pickup. */
export const STOP_PICKUP = /^This stop already has a pickup\.$/;

/** The driver text for a refused send, from its status and the server detail. A stop entry with gallons has its own text (SO-1083). */
export function fieldText(status: number, detail: string | null, kind?: string): string {
  if (kind === "stop" && detail && STOP_PICKUP.test(detail)) return FIELD_TEXT.stopPickup;
  const rule = detail ? RULES.find(([pattern]) => pattern.test(detail)) : undefined;
  if (rule) return FIELD_TEXT[rule[1]];
  if (status === 409) return FIELD_TEXT.changed;
  if (status === 403) return FIELD_TEXT.rights;
  if (status === 404) return FIELD_TEXT.missing;
  if (status === 422) return FIELD_TEXT.value;
  return FIELD_TEXT.other;
}

/** The longest server text that the outbox keeps, so that a long validation list stays readable. */
export const DETAIL_MAX = 300;

const itemText = (item: unknown): string | null => {
  if (!item || typeof item !== "object") return typeof item === "string" ? item : null;
  const { loc, msg } = item as { loc?: unknown; msg?: unknown };
  if (typeof msg !== "string") return null;
  const where = Array.isArray(loc) ? loc.filter((part, index) => !(index === 0 && part === "body")).join(".") : "";
  return where ? `${where}: ${msg}` : msg;
};

/** The server text of a refused send: a string detail, or each "field: message" of a validation list. */
export function serverDetail(body: unknown): string | null {
  const detail = body && typeof body === "object" ? (body as { detail?: unknown }).detail : undefined;
  const text = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(itemText).filter((item): item is string => !!item).join("; ") : "";
  if (!text) return null;
  return text.length > DETAIL_MAX ? `${text.slice(0, DETAIL_MAX - 1)}…` : text;
}

/** A field of the visit form that a refused entry can name. */
export type RefusedField = "gallons" | "scrap" | "left" | "reason" | "time" | "truck" | "driver" | "account";

const BODY_FIELDS: Record<string, RefusedField> = {
  raw_gal: "gallons",
  scrap_gal: "scrap",
  gallons_left: "left",
  reason: "reason",
  at: "time",
  truck_id: "truck",
  load_id: "truck",
  driver_id: "driver",
  account_id: "account",
  route_stop_id: "account",
};

const FIELD_RULES: [RegExp, RefusedField][] = [
  [/^Scrap cannot be more/, "scrap"],
  [/^Enter the raw gallons/, "gallons"],
  [/gallons left/i, "left"],
  [/^Choose why/, "reason"],
  [/^The driver is not/, "driver"],
  [/^The truck is not/, "truck"],
  [/^The (stop|account) is not/, "account"],
  [/\b(time|date)\b.*(future|after today|day of the route)|must be in the last/i, "time"],
];

/** The form field that the kept server text of a refused send names, or null. A 409 names none. */
export function refusedField(status: number, detail: string | null | undefined): RefusedField | null {
  if (!detail || status === 409) return null;
  const path = /^([\w.]+): /.exec(detail)?.[1];
  if (path) return path.split(".").reduceRight<RefusedField | null>((found, part) => found ?? BODY_FIELDS[part] ?? null, null);
  return FIELD_RULES.find(([pattern]) => pattern.test(detail))?.[1] ?? null;
}

/** The words of the form around a refused field: its save button, and the name of its time field. */
export interface FixWords {
  button?: string;
  time?: string;
}

/** The text next to the refused field (rule U-17). */
export function fieldFixText(field: RefusedField, word: string, { button = "Save", time = "date or time" }: FixWords = {}): string {
  const text: Record<RefusedField, string> = {
    gallons: `The total ${word} is not correct. Change it, then tap ${button}.`,
    scrap: `The scrap ${word} is not correct. Change it, then tap ${button}.`,
    left: `The level left is not correct. Change it, then tap ${button}.`,
    reason: `Select the reason, then tap ${button}.`,
    time: `The ${time} is not correct. Change it, then tap ${button}.`,
    truck: `The truck is not correct. Select a different truck, then tap ${button}.`,
    driver: `The driver is not correct. Select a different driver, then tap ${button}.`,
    account: `The account is not correct. Select a different account, then tap ${button}.`,
  };
  return text[field];
}

/** The text next to a typed reason that the server refused. */
export const otherFixText = (button: string) => `Change the reason text, then tap ${button}.`;

/** The refusal text of the Sync page with the name of the button that is on the screen. */
export const withButton = (text: string, button: string) => text.replace(/\btap Save\b/g, `tap ${button}`);

/** The button that puts a refused visit back in the form, and the line that tells what it does (SO-1003). */
export const CHANGE_LABEL = "Change this entry";
export const changeHint = (button: string) => `This puts the entry back in the fields below. Change it, then tap ${button}.`;
export const CHANGE_HINT = changeHint("Save");

/** The refusal text of a stop change: the phone sends the time now, so the form has no time field (SO-1060). */
export const changeRefusalText = (reason: string, button: string) =>
  reason === FIELD_TEXT.time ? `The time of this change was not correct. Tap ${CHANGE_LABEL}, then ${button}. The phone uses the time now.` : withButton(reason, button);

/** The refusal text of a first stop entry. The phone sends the time of the save, thus the form has no time field. */
export const entryRefusalText = (reason: string, button: string) =>
  reason === FIELD_TEXT.time ? `The time of this entry was not correct. Tap ${CHANGE_LABEL}, then ${button}. The phone uses the time now.` : withButton(reason, button);

/** A change cannot help these refusals: the server has the entry or the pickup, or the office changed it first. */
export const canChange = (reason: string | null | undefined) => reason !== FIELD_TEXT.copy && reason !== FIELD_TEXT.changed && reason !== FIELD_TEXT.dayOver && reason !== FIELD_TEXT.stopPickup;

/** The refusal text of an entry that the Sync page sends to Today to fix: Sync has no Save button. */
export const fixOnTodayText = (text: string) => text.replace(/Change the (entry|time) and tap Save again\./, "Tap Fix on Route, change the $1, then save it again.");

/** The Sync text of an entry that Today fixes. Today sends the time now and has no time field (SO-1081, SO-1107). */
export const syncFixText = (reason: string, change: boolean) =>
  reason === FIELD_TEXT.time ? `The time of this ${change ? "change" : "entry"} was not correct. Tap Fix on Route, then save it again. The phone uses the time now.` : fixOnTodayText(reason);
